import { useEffect, useRef } from "react";
import { CircleAlert, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface WorkflowNotice {
  tone: "info" | "error";
  title: string;
  /** Body paragraphs. Rendered in order, one block each. */
  lines: string[];
  /** Optional fixed-width detail rows, e.g. a list of renamed nodes. */
  details?: string[];
  /**
   * Turns the dialog into a question rather than a statement: a second button
   * that runs `onConfirm`, alongside a cancel that just closes. Used for the
   * leave-with-unsaved-changes prompt. Omit it and the dialog stays a plain
   * acknowledgement with one button.
   */
  confirm?: {
    /** Label for the action button, e.g. "Leave without saving". */
    label: string;
    /** Label for the dismiss button. Defaults to "Stay". */
    cancelLabel?: string;
    /** Styles the action button as destructive. */
    destructive?: boolean;
    onConfirm: () => void;
  };
}

/**
 * Centred in-app dialog for a message the user must read, or a yes/no question
 * when `notice.confirm` is set.
 *
 * Replaces `window.alert()` on the canvas. The browser's own dialog is drawn by
 * the browser, not the product: it lands at the top of the window, carries the
 * page URL, cannot be styled, and reads as a low-quality or debug surface.
 * This matches the shell used by run-columns-dialog so canvas dialogs look like
 * one system.
 *
 * Escape closes it and focus moves to the dismiss button on open, so it is
 * usable without a mouse.
 */
export function NoticeDialog({
  notice,
  onClose,
}: {
  notice: WorkflowNotice;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isError = notice.tone === "error";
  const Icon = isError ? CircleAlert : Info;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={notice.title}
        className="max-h-[85vh] w-[min(620px,94vw)] overflow-y-auto rounded-xl border border-border bg-background p-7 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2.5">
          <Icon
            size={20}
            className={cn(isError ? "text-rose-500" : "text-accent")}
          />
          <h2 className="text-[19px] font-semibold leading-tight">{notice.title}</h2>
        </div>

        {/* text-secondary rather than text-muted: this is body copy the user has
            to read, not a hint, so it needs proper contrast. */}
        {notice.lines.map((line, i) => (
          <p key={i} className="mb-2.5 text-[15px] leading-relaxed text-text-secondary">
            {line}
          </p>
        ))}

        {notice.details && notice.details.length > 0 && (
          <ul className="mt-3 flex flex-col gap-1.5 rounded-lg border border-border bg-surface px-4 py-3">
            {notice.details.map((d, i) => (
              <li key={i} className="font-mono text-[13px] text-text-primary">
                {d}
              </li>
            ))}
          </ul>
        )}

        {/* Focus sits on the dismiss button either way, so Enter on an
            unread confirm keeps the work rather than discarding it. */}
        <div className="mt-6 flex items-center justify-end gap-2">
          <Button
            ref={closeRef}
            variant={notice.confirm ? "outline" : "default"}
            onClick={onClose}
          >
            {notice.confirm ? notice.confirm.cancelLabel ?? "Stay" : "Got it"}
          </Button>
          {notice.confirm && (
            <Button
              variant={notice.confirm.destructive ? "destructive" : "default"}
              onClick={notice.confirm.onConfirm}
            >
              {notice.confirm.label}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
